import { useEffect, useState } from "react";
import { BookOpen, ChevronsUpDown, Loader2 } from "lucide-react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@lumanity/ui/primitives";
import { cn } from "@/lib/utils";
import { useOutlets } from "./outputsApi";
import { OUTLET_KIND_LABELS } from "./model";
import type { Outlet } from "./types";

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

interface OutletComboboxProps {
  value: Outlet | null;
  onChange: (outlet: Outlet) => void;
  disabled?: boolean;
}

/** Searchable journal / congress picker; the search runs on the server (`GET /api/outlets?q=`). */
const OutletCombobox = ({ value, onChange, disabled }: OutletComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const q = useDebounced(search, 250);
  const { data: outlets = [], isFetching, error } = useOutlets(q, { enabled: open });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Target outlet"
          disabled={disabled}
          className="w-full justify-between h-11 font-normal"
        >
          <span className="flex items-center gap-2 min-w-0">
            <BookOpen className="w-4 h-4 shrink-0 text-brand-teal" />
            {value ? (
              <span className="truncate font-semibold text-brand-navy">{value.name}</span>
            ) : (
              <span className="text-muted-foreground">Search for a journal or congress</span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type to search outlets..." value={search} onValueChange={setSearch} />
          <CommandList>
            {error ? (
              <div className="p-3 text-sm text-destructive">Could not load outlets: {error.message}</div>
            ) : (
              <CommandEmpty>
                {isFetching ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "No outlets match your search."}
              </CommandEmpty>
            )}
            <CommandGroup>
              {outlets.map((outlet) => (
                <CommandItem
                  key={outlet.id}
                  value={outlet.id}
                  onSelect={() => {
                    onChange(outlet);
                    setOpen(false);
                  }}
                  className={cn("flex items-center justify-between gap-3", value?.id === outlet.id && "bg-brand-light-teal")}
                >
                  <span className="min-w-0">
                    <span className="font-semibold text-brand-navy">{outlet.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{OUTLET_KIND_LABELS[outlet.kind]}</span>
                  </span>
                  {outlet.publisher && <span className="text-xs text-muted-foreground truncate">{outlet.publisher}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default OutletCombobox;
